import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, Label } from "@/components/Reveal";
import WisdomProgression from "@/components/WisdomProgression";
import TopoBackground from "@/components/TopoBackground";
import { MEDIA, MANIFESTO } from "@/data/content";

const PHILOSOPHY = [
  "Stories", "Language", "Ecological understanding", "Cultural practice",
  "Scientific discovery", "Family memory", "Traditional law", "Stewardship",
];

export default function Vision() {
  return (
    <div data-testid="vision-page" className="pt-20">
      {/* Intro */}
      <section className="relative py-28 md:py-40 overflow-hidden">
        <img src={MEDIA.forestPath} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050806] via-[#050806]/80 to-[#050806]" />
        <TopoBackground opacity={0.06} />
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <Label className="block mb-6">Our Vision</Label>
            <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-[#F4F1ED]">
              Every community carries knowledge that deserves to endure.
            </h1>
            <p className="mt-8 max-w-2xl text-lg md:text-xl text-[#A8A5A1] leading-relaxed">
              The greatest challenge facing humanity is not simply losing information. It is
              losing wisdom. Information can be stored. Wisdom must be stewarded.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Philosophy chips */}
      <section className="pb-24 md:pb-32 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-3">
            {PHILOSOPHY.map((p, i) => (
              <Reveal key={p} delay={i * 0.05}>
                <span className="inline-block px-5 py-2.5 rounded-full border border-white/10 text-[#D8D4CE] text-sm hover:border-[#D4A373]/60 hover:text-[#D4A373] transition-colors duration-500">
                  {p}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Manifesto */}
      <section className="relative overflow-hidden py-28 md:py-44 border-b border-white/5">
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12">
          <Reveal>
            <Label className="block mb-12">The Manifesto</Label>
          </Reveal>
          <div className="space-y-16 md:space-y-24">
            {MANIFESTO.map((line, i) => (
              <Reveal key={i} delay={0.05}>
                <p
                  data-testid={`manifesto-line-${i}`}
                  className="font-display font-light text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.3] text-[#F4F1ED]"
                >
                  {line}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Stewardship Intelligence */}
      <section className="relative py-28 md:py-40 overflow-hidden border-b border-white/5">
        <img src={MEDIA.mist} alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-[#050806]/75" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <Label className="block mb-6">A new capacity</Label>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl text-[#F4F1ED]">
              Stewardship Intelligence
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 text-lg md:text-xl text-[#A8A5A1] leading-relaxed">
              Stewardship Intelligence is the capacity to make decisions that strengthen
              people, places and future generations. WisdomKeepers exists to cultivate it
              across communities, organisations and institutions.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Recurring philosophy thread */}
      <section className="py-20 md:py-28 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <WisdomProgression dark />
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <h2 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl text-[#F4F1ED]">
              Technology is never the purpose. Stewardship is.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/what-we-build"
              data-testid="vision-next-link"
              className="mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-md bg-[#D4A373] text-[#050806] font-medium hover:bg-[#E6CCB2] transition-colors duration-500"
            >
              See what we build <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
